import { builderImageUrl } from "~/hec/utils/builder-image-url";

/**
 * Real-`<img>` replacement for the heroes' CSS `background-image` (COM-181).
 *
 * CSS background images are LCP-INVISIBLE by spec, so a hero painted as a
 * background can never be the page's LCP element — the LCP falls through to
 * whatever text paints last. This renders the same crop as
 * `background-size: cover; background-position: center` via
 * `object-fit/object-position` on an absolutely-positioned `<img>` that fills
 * the hero section, stacked under the (positioned, later-in-DOM) content.
 *
 * Responsive behavior mirrors `getResponsiveHeroBgStyles` exactly: the same
 * `width=1600` desktop / `width=800` mobile Builder CDN transforms, with the
 * mobile asset applied at `(max-width: 767px)` via `<picture>` instead of an
 * injected media-query style tag.
 *
 * `priority` (COM-163 convention, default false) adds
 * `fetchPriority="high"` for above-the-fold heroes. `loading` stays eager
 * either way — CSS backgrounds were never lazy, so lazy-loading here would be
 * a visual-timing regression, not parity.
 */

/** Must match the breakpoint `getResponsiveHeroBgStyles` emits. */
const MOBILE_MEDIA = "(max-width: 767px)";

const IMG_CLASS =
  "pointer-events-none absolute inset-0 h-full w-full select-none object-cover object-center";

export interface HeroBackgroundImageProps {
  backgroundImage?: string;
  backgroundImageMobile?: string;
  /** Above-the-fold hero: adds fetchPriority="high" (COM-163 convention). */
  priority?: boolean;
}

export function HeroBackgroundImage({
  backgroundImage,
  backgroundImageMobile,
  priority = false,
}: HeroBackgroundImageProps) {
  const desktopSrc = builderImageUrl(backgroundImage, {
    width: 1600,
    format: "webp",
  });
  const mobileSrc = builderImageUrl(backgroundImageMobile, {
    width: 800,
    format: "webp",
  });
  if (!desktopSrc && !mobileSrc) return null;

  const fetchPriority = priority ? ("high" as const) : undefined;

  // Mobile-only background: the CSS version painted nothing above 767px, so
  // the img must stay hidden there too.
  if (!desktopSrc) {
    return (
      <img
        src={mobileSrc}
        alt=""
        role="presentation"
        loading="eager"
        fetchPriority={fetchPriority}
        className={`${IMG_CLASS} max-[767px]:block hidden`}
        data-testid="hero-background-image"
      />
    );
  }

  const img = (
    <img
      src={desktopSrc}
      alt=""
      role="presentation"
      loading="eager"
      fetchPriority={fetchPriority}
      className={IMG_CLASS}
      data-testid="hero-background-image"
    />
  );

  if (mobileSrc && mobileSrc !== desktopSrc) {
    return (
      <picture>
        <source media={MOBILE_MEDIA} srcSet={mobileSrc} />
        {img}
      </picture>
    );
  }

  return img;
}
